import styled from '@emotion/styled'
import { Key, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { Label } from './Label'
import { Color, inputFontSize, theme } from '../../constants'
import { useValidate } from '../../hooks/useValidate'
import { StyleProps, createStyles } from '../../utils'
import { ErrorMessage } from '../ErrorMessage'

const TextareaElement = styled.textarea<{
  fullWidth?: boolean
  invalid?: boolean
  styles?: StyleProps
  minHeight?: number
}>((props) => ({
  background: Color.LIGHT_GREY,
  border: theme.border.style.thinMedium,
  ...(props.invalid && { borderColor: Color.RED }),
  borderRadius: theme.border.radius.normal,
  boxSizing: 'border-box',
  // Ensure that we don't use the browser's default font:
  fontFamily: 'inherit',
  lineHeight: 1.4,
  ...inputFontSize({}),
  minHeight: props.minHeight ?? 128,
  maxHeight: '50vh',
  padding: `${theme.spacing['2']}px ${theme.spacing['2.5']}px`,
  resize: 'none',
  width: props.fullWidth ? '100%' : undefined,
  '&:disabled': {
    background: Color.DARK_MEDIUM_GREY,
  },

  ...createStyles(props.styles),
}))

const TextareaLabel = styled(Label)({
  width: '100%',
})

export type TextareaProps<T extends Key = string> = {
  autoFocus?: boolean
  /**
   * NOTE: This value is not dynamic.
   */
  autogrow?: boolean
  /**
   * TODO(rb): rename to `labelDescription` (or remove entirely)
   */
  description?: string
  disabled?: boolean
  /**
   * A value of `''` or `true` will reserve space for an error message.
   */
  fullWidth?: boolean
  minHeight?: number
  label?: string
  onBlur?: () => void
  onChange?: (value: T) => void
  onFocus?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  required?: boolean
  styles?: StyleProps
  value?: T | null
  schema?: z.ZodTypeAny
  errorMessage?: string
  reserveErrorSpace?: boolean
  forceShowError?: boolean
}

function resize(element: HTMLTextAreaElement) {
  /*
    First we resize the element to 0px height to make it overflow, then we can use scrollHeight to
    figure out how big it should be to not have a scrollbar.

    + 1 is used so that we won't have sub-pixel rounding issues. Showing scrollbar when there
    shouldn't be one.
  */
  element.style.height = '0px'
  element.style.height =
    element.scrollHeight + 2 * theme.border.width.thin + 1 + 'px'
}

export const Textarea = (props: TextareaProps) => {
  const [touched, setTouched] = useState(false)
  const validateError = useValidate(props.schema, props.required, props.value)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const element = ref.current

    if (!element || !props.autogrow) {
      return
    }

    // If using a really old browser, we just don't resize the input on e.g. window resize
    if (!window.ResizeObserver) {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      resize(element)
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.unobserve(element)
    }
  }, [])

  useEffect(() => {
    if (props.autogrow && ref.current) {
      resize(ref.current)
    }
  }, [props.value, ref.current])

  const errorMessage = props.errorMessage ?? validateError
  const showError = (touched || props.forceShowError) && !!errorMessage

  const elementAndError = (
    <>
      <TextareaElement
        autoFocus={props.autoFocus}
        disabled={props.disabled}
        fullWidth={props.fullWidth}
        minHeight={props.minHeight}
        invalid={showError}
        onBlur={() => {
          setTouched(true)
          props.onBlur?.()
        }}
        onChange={(event) => {
          props.onChange?.(event.target.value)
        }}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        placeholder={
          props.placeholder || (props.label ? `${props.label}...` : undefined)
        }
        ref={ref}
        required={props.required}
        styles={props.styles}
        value={props.value ?? undefined}
      />
      <ErrorMessage
        errorMessage={showError ? errorMessage : undefined}
        reserveSpace={props.reserveErrorSpace}
      />
    </>
  )

  return props.label ? (
    <TextareaLabel
      description={props.description}
      fullWidth={props.fullWidth}
      required={props.required}
      text={props.label}
    >
      {elementAndError}
    </TextareaLabel>
  ) : (
    elementAndError
  )
}
